import React from "react";
import { enqueueSnackbar } from "notistack";

const COLOR_PALETTES = {
  stone: {
    50: "#f9f7f7",
    100: "#f6f2f1",
    200: "#dddad9",
    300: "#c5c2c1",
    400: "#a6a4a3",
    500: "#878585",
    600: "#626160",
    700: "#44403c",
    800: "#292524",
    900: "#1C1917",
    950: "#1c1918",
  },
  gray: {
    50: "#f5f7f8",
    100: "#f3f4f7",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca4af",
    500: "#6c7180",
    600: "#4b5564",
    700: "#374151",
    800: "#1e2937",
    900: "#121827",
    950: "#121827",
  },
  teal: {
    50: "#eef5f6",
    100: "#d6e7eb",
    200: "#b8e2e9",
    300: "#86cdda",
    400: "#4eaec2",
    500: "#359ab1",
    600: "#2d778d",
    700: "#2a6174",
    800: "#2a5160",
    900: "#274452",
    950: "#172b36",
  },
  blue: {
    50: "#f1f8fe",
    100: "#e2f0fc",
    200: "#c0dff8",
    300: "#86c7f3",
    400: "#46abea",
    500: "#1e90d9",
    600: "#1072b9",
    700: "#0f5b95",
    800: "#104d7c",
    900: "#124167",
    950: "#0c2a45",
  },
  green: {
    50: "#f4faea",
    100: "#e8f6d5",
    200: "#d0ebb0",
    300: "#b0dc8b",
    400: "#7fc869",
    500: "#4baf53",
    600: "#259850",
    700: "#038f52",
    800: "#008251",
    900: "#016541",
    950: "#003922",
  },
  yellow: {
    50: "#fefce8",
    100: "#fef9c2",
    200: "#fff085",
    300: "#ffdf1f",
    400: "#fdc700",
    500: "#f0b100",
    600: "#d08701",
    700: "#a65f00",
    800: "#894b00",
    900: "#441f04",
    950: "#441f04",
  },
  orange: {
    50: "#fef7ec",
    100: "#fbe8ca",
    200: "#f7cf90",
    300: "#f3b056",
    400: "#f09a38",
    500: "#e87418",
    600: "#ce5211",
    700: "#ab3812",
    800: "#8b2c16",
    900: "#722414",
    950: "#411007",
  },
  red: {
    50: "#fef2f2",
    100: "#ffe2e2",
    200: "#ffc9c9",
    300: "#ffa2a2",
    400: "#ff6467",
    500: "#fb2c37",
    600: "#e7020a",
    700: "#c10207",
    800: "#9f0811",
    900: "#82171b",
    950: "#460909",
  },
  salmon: {
    50: "#fef2f2",
    100: "#fee5e6",
    200: "#fccfd4",
    300: "#f9a7af",
    400: "#f5717f",
    500: "#ed475e",
    600: "#d92546",
    700: "#b7183b",
    800: "#991839",
    900: "#831737",
    950: "#490817",
  },
  purple: {
    50: "#f7f6fc",
    100: "#f0eef9",
    200: "#e4dff5",
    300: "#cec5ed",
    400: "#c1b2e6",
    500: "#9a7ed4",
    600: "#8a62c5",
    700: "#7a50b2",
    800: "#654295",
    900: "#54387a",
    950: "#362352",
  },
  indigo: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c8d2fd",
    300: "#a7b5fa",
    400: "#858ff4",
    500: "#676aed",
    600: "#544cdf",
    700: "#483ec4",
    800: "#372fa3",
    900: "#322e81",
    950: "#1d1b4b",
  },
};

const SHADES = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
  "950",
] as const;

const copyToClipboard = (hex: string) => {
  navigator.clipboard.writeText(hex);
  enqueueSnackbar(`${hex} copied to clipboard`, { variant: "default" });
};

const FoundationPage: React.FC = () => {
  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-primary">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">
          Foundation
        </h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Core design tokens including typography, colors, and text styles.
        </p>
      </header>

      <div className="space-y-16">
        {/* Colors */}
        <section id="colors" className="scroll-mt-8 space-y-6">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Colors</h2>
            <p className="mt-1 text-sm text-stone-500">
              Color palettes used throughout the platform. Click any swatch to copy the hex code.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="w-24 pb-3 text-left text-sm font-medium text-stone-500"></th>
                  {SHADES.map((shade) => (
                    <th key={shade} className="pb-3 text-center text-sm font-medium text-stone-500">
                      {shade}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(COLOR_PALETTES).map(([name, shades]) => (
                  <tr key={name}>
                    <td className="py-2 pr-4 text-sm font-medium capitalize text-stone-700">
                      {name}
                    </td>
                    {SHADES.map((shade) => {
                      const hex = shades[parseInt(shade) as keyof typeof shades];
                      return (
                        <td key={shade} className="p-1">
                          <button
                            onClick={() => copyToClipboard(hex)}
                            className="h-12 w-full min-w-[3.5rem] rounded-md transition-transform hover:scale-105 active:scale-95"
                            style={{ backgroundColor: hex }}
                            title={hex}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Typography */}
        <section id="typography" className="scroll-mt-8 space-y-6">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Typography</h2>
            <p className="mt-1 text-sm text-stone-500">
              Font families, sizes, weights, and text styles used across the platform. All text
              defaults to{" "}
              <code className="rounded-sm bg-stone-100 px-1 py-0.5 text-2xs">text-primary</code>{" "}
              color.
            </p>
          </div>

          {/* Headings */}
          <div className="space-y-4">
            <h3 className="font-sans text-base font-medium">Headings</h3>
            <p className="text-sm text-secondary">
              Headings use semantic HTML elements with pre-defined styles. No classes needed for
              default styling.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left font-medium text-secondary">Element</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Font</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Size</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Weight</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Line Height</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">
                      Letter Spacing
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">{"<h1>"}</td>
                    <td className="px-4 py-3">Serif</td>
                    <td className="px-4 py-3">36px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">48px</td>
                    <td className="px-4 py-3">-0.432px</td>
                    <td className="px-4 py-3">
                      <h1 className="!text-2xl">Heading 1</h1>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">{"<h2>"}</td>
                    <td className="px-4 py-3">Serif</td>
                    <td className="px-4 py-3">30px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">36px</td>
                    <td className="px-4 py-3">-0.225px</td>
                    <td className="px-4 py-3">
                      <h2 className="!text-xl">Heading 2</h2>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">{"<h3>"}</td>
                    <td className="px-4 py-3">Sans</td>
                    <td className="px-4 py-3">24px</td>
                    <td className="px-4 py-3">600</td>
                    <td className="px-4 py-3">32px</td>
                    <td className="px-4 py-3">-0.24px</td>
                    <td className="px-4 py-3">
                      <h3 className="!text-lg">Heading 3</h3>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">{"<h4>"}</td>
                    <td className="px-4 py-3">Sans</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3">600</td>
                    <td className="px-4 py-3">30px</td>
                    <td className="px-4 py-3">-0.16px</td>
                    <td className="px-4 py-3">
                      <h4 className="!text-base">Heading 4</h4>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-2xs">{"<p>"}</td>
                    <td className="px-4 py-3">Sans</td>
                    <td className="px-4 py-3">14px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">24px</td>
                    <td className="px-4 py-3">-0.16px</td>
                    <td className="px-4 py-3">
                      <p>Body text</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Text Sizes */}
          <div className="space-y-4">
            <h3 className="font-sans text-base font-medium">Text Sizes (Tailwind)</h3>
            <p className="text-sm text-secondary">
              Standard Tailwind font size scale. Use these classes to override default element
              sizes.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left font-medium text-secondary">Class</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Size</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-2xs</td>
                    <td className="px-4 py-3">12px</td>
                    <td className="px-4 py-3 text-2xs">Extra small text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-xs</td>
                    <td className="px-4 py-3">13px</td>
                    <td className="px-4 py-3 text-xs">Extra small text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-sm</td>
                    <td className="px-4 py-3">14px</td>
                    <td className="px-4 py-3 text-sm">Small text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-base</td>
                    <td className="px-4 py-3">16px</td>
                    <td className="px-4 py-3 text-base">Base text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-lg</td>
                    <td className="px-4 py-3">18px</td>
                    <td className="px-4 py-3 text-lg">Large text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-xl</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3 text-xl">Extra large text</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">text-2xl</td>
                    <td className="px-4 py-3">24px</td>
                    <td className="px-4 py-3 text-2xl">2XL text</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-2xs">text-3xl</td>
                    <td className="px-4 py-3">30px</td>
                    <td className="px-4 py-3 text-3xl">3XL text</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Custom Text Utilities */}
          <div className="space-y-4">
            <h3 className="font-sans text-base font-medium">Custom Text Utilities</h3>
            <p className="text-sm text-secondary">
              Semantic text classes for common use cases. These include font size, weight, line
              height, and color.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="px-4 py-2 text-left font-medium text-secondary">Class</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Size</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Weight</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Line Height</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Color</th>
                    <th className="px-4 py-2 text-left font-medium text-secondary">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">.text-lead</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">28px</td>
                    <td className="px-4 py-3">primary</td>
                    <td className="px-4 py-3">
                      <span className="text-lead">Lead text for introductions</span>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">.text-large</td>
                    <td className="px-4 py-3">18px</td>
                    <td className="px-4 py-3">600</td>
                    <td className="px-4 py-3">28px</td>
                    <td className="px-4 py-3">primary</td>
                    <td className="px-4 py-3">
                      <span className="text-large">Large text for emphasis</span>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">.text-small</td>
                    <td className="px-4 py-3">14px</td>
                    <td className="px-4 py-3">500</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3">primary</td>
                    <td className="px-4 py-3">
                      <span className="text-small">Small text for secondary content</span>
                    </td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3 font-mono text-2xs">.text-subtle</td>
                    <td className="px-4 py-3">14px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3">secondary</td>
                    <td className="px-4 py-3">
                      <span className="text-subtle">Subtle text for hints</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-2xs">.text-detail</td>
                    <td className="px-4 py-3">12px</td>
                    <td className="px-4 py-3">400</td>
                    <td className="px-4 py-3">20px</td>
                    <td className="px-4 py-3">secondary</td>
                    <td className="px-4 py-3">
                      <span className="text-detail">Detail text for metadata</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Font Weights */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Font Weights</h3>
            <div className="space-y-2 rounded-lg border p-4">
              <p className="font-normal">font-normal (400) - Normal weight text</p>
              <p className="font-medium">font-medium (500) - Medium weight text</p>
              <p className="font-semibold">font-semibold (600) - Semibold weight text</p>
              <p className="font-bold">font-bold (700) - Bold weight text</p>
            </div>
          </div>

          {/* Text Colors */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Text Colors</h3>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-stone-600">Text Tokens</h4>
              <div className="grid grid-cols-2 gap-2 rounded-lg border p-4 md:grid-cols-4">
                <p className="text-primary">text-primary</p>
                <p className="text-secondary">text-secondary</p>
                <p className="text-tertiary">text-tertiary</p>
                <p className="text-heading">text-heading</p>
                <p className="text-label">text-label</p>
                <p className="text-disabled">text-disabled</p>
                <p className="text-placeholder">text-placeholder</p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-stone-600">Semantic/Status Colors</h4>
              <div className="grid grid-cols-2 gap-2 rounded-lg border p-4 md:grid-cols-3">
                <p className="text-success">text-success</p>
                <p className="text-warning">text-warning</p>
                <p className="text-destructive">text-destructive</p>
              </div>
            </div>
          </div>

          {/* Background Colors */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Background Colors</h3>
            <div className="grid grid-cols-2 gap-3 rounded-lg border p-4 md:grid-cols-3">
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-white border border-stone-200" />
                <span className="text-sm text-stone-600">bg-white</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-subtle" />
                <span className="text-sm text-stone-600">bg-subtle</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-background" />
                <span className="text-sm text-stone-600">bg-background</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-inverse" />
                <span className="text-sm text-stone-600">bg-inverse</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-brand" />
                <span className="text-sm text-stone-600">bg-brand</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md bg-brand-subtle" />
                <span className="text-sm text-stone-600">bg-brand-subtle</span>
              </div>
            </div>
          </div>

          {/* Border Colors */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Border Colors</h3>
            <div className="grid grid-cols-2 gap-3 rounded-lg border p-4 md:grid-cols-3">
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md border-2 border-primary bg-white" />
                <span className="text-sm text-stone-600">border-primary</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md border-2 border-strong bg-white" />
                <span className="text-sm text-stone-600">border-strong</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md border-2 border-focus bg-white" />
                <span className="text-sm text-stone-600">border-focus</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md border-2 border-brand bg-white" />
                <span className="text-sm text-stone-600">border-brand</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="h-12 rounded-md border-2 border-error bg-white" />
                <span className="text-sm text-stone-600">border-error</span>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">Links</h3>
            <div className="rounded-lg border p-4">
              <p>
                This is a paragraph with a <a href="#">default link</a> that uses primary color and
                underline style.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FoundationPage;
